import { Button, Col, Divider, Form, FormInstance, Row, Select, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import TextArea from 'antd/es/input/TextArea';
import { Fragment } from 'react/jsx-runtime';

// components
import ListButton from 'components/listButton/listButton';
import Icon from 'utils/icon';

// services
import { checkArabicValidation } from 'utils/validators/arabic';
import { checkEnglishValidation } from 'utils/validators/english';
import { CommitteeMeetingDecision, CommitteeMeetingType } from '../models/meetings';
import SelectElement from 'components/selectElement/selectElement';
import { useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';
import NotificationService from 'core/services/notification';
import StatusComponent from 'components/status/status';
import { isRTL } from 'core/services/language';
import { LoadingOutlined } from '@ant-design/icons';
import { onSearchCommitteeMember } from '../services/meetingServices';

interface CommitteeDecisionElementProps {
	type: CommitteeMeetingType;
	form: FormInstance;
	decisions?: CommitteeMeetingDecision[];
	onDeleteDecision?: (id: number) => void;
}

function CommitteeDecisionElement({ type, form, decisions, onDeleteDecision }: CommitteeDecisionElementProps) {
	const { t } = useTranslation();

	const watchedDecisions = Form.useWatch('decisions', form);

	const [searchedQuery, setSearchedQuery] = useState<string>('');
	const [debouncedQuery] = useDebounce(searchedQuery, 500);

	const [searchedUsersOptions, setSearchedUsersOptions] = useState<{ label: React.ReactNode; value: string }[]>([]);
	const [isSearching, setIsSearching] = useState<boolean>(false);

	// Store all selected shareholders with their full info (for displaying as tags)
	const [allShareholdersMap, setAllShareholdersMap] = useState<Map<string, { label: React.ReactNode; value: string }>>(new Map());

	const validateArabic = (_: any, value: string) => {
		if (!value) return Promise.resolve();
		if (checkArabicValidation(_, value)) {
			return Promise.resolve();
		} else if (value) return Promise.reject(new Error(t('COMMON.ARABIC_ONLY')));
		else return Promise.reject();
	};

	// Handle search with debounce
	useEffect(() => {
		if (debouncedQuery.length >= 3) {
			searchUserHandler(debouncedQuery);
		} else {
			setSearchedUsersOptions([]);
		}
	}, [debouncedQuery]);

	// Initialize shareholders map from decisions prop
	useEffect(() => {
		if (decisions && decisions.length > 0) {
			const shareholdersMap = new Map<string, { label: React.ReactNode; value: string }>();

			decisions.forEach((decision, decisionIndex) => {
				if (decision.abstention_type === 'specific_committee_members' && decision.abstaining_committee_members) {
					const shareholderIds: string[] = [];

					decision.abstaining_committee_members.forEach((shareholder) => {
						const shareholderId = `${shareholder.id}`;
						shareholderIds.push(shareholderId);

						// Add to map if not already present
						if (!shareholdersMap.has(shareholderId)) {
							shareholdersMap.set(shareholderId, {
								label: (
									<p className="mb-0" style={{ fontSize: 15 }}>
										{isRTL() ? shareholder.name : (shareholder.name_en ?? shareholder.name)}
									</p>
								),
								value: shareholderId,
							});
						}
					});

					// Set the form field value with the IDs
					form.setFieldValue(['decisions', decisionIndex, 'abstaining_board_member_ids'], shareholderIds);
				}
			});

			setAllShareholdersMap(shareholdersMap);
		}
	}, [decisions]);

	const validateEnglish = (_: any, value: string) => {
		if (!value) return Promise.resolve();
		if (checkEnglishValidation(_, value)) {
			return Promise.resolve();
		} else if (value) return Promise.reject(new Error(t('COMMON.ENGLISH_ONLY')));
		else return Promise.reject();
	};

	const searchUserHandler = async (text: string) => {
		try {
			setIsSearching(true);

			const response = await onSearchCommitteeMember(text);

			const newOptions = response.map((user) => ({
				label: (
					<p className="mb-0" style={{ fontSize: 15 }}>
						{`${isRTL() ? user.member_name : (user.member_name_en ?? user.member_name)}`}
					</p>
				),
				value: `${user.member_id}`,
			}));

			setSearchedUsersOptions(newOptions);
			setIsSearching(false);
		} catch (error) {
			setIsSearching(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const handleDeleteDecision = (name: number, remove: (index: number) => void) => {
		const decisionId = form.getFieldValue(['decisions', name, 'id']);
		if (decisionId) onDeleteDecision?.(+decisionId);
		remove(name);
	};

	const handleSelectChange = (values: string[], name: number) => {
		// Update the form field
		form.setFieldValue(['decisions', name, 'abstaining_board_member_ids'], values);

		// Add newly selected options to the map in a single setState call
		const newMap = new Map(allShareholdersMap);
		let hasChanges = false;

		searchedUsersOptions.forEach((option) => {
			if (values.includes(option.value) && !newMap.has(option.value)) {
				newMap.set(option.value, option);
				hasChanges = true;
			}
		});

		if (hasChanges) {
			setAllShareholdersMap(newMap);
		}

		// Clear search
		setSearchedQuery('');
		setSearchedUsersOptions([]);
	};

	// Get options for a specific decision (combines selected shareholders + search results)
	const getOptionsForDecision = (decisionIndex: number) => {
		const selectedIds = form.getFieldValue(['decisions', decisionIndex, 'abstaining_board_member_ids']) || [];

		// Get selected options from the map
		const selectedOptions = selectedIds.map((id: string) => allShareholdersMap.get(id)).filter(Boolean);

		// Combine with search results, avoiding duplicates
		const combined = [...selectedOptions];
		searchedUsersOptions.forEach((option) => {
			if (!combined.some((existing) => existing.value === option.value)) {
				combined.push(option);
			}
		});

		return combined;
	};

	return (
		<>
			<h6 className="mb-3" style={{ fontSize: '1.1rem' }}>
				{t('ACTIONS.VOTING_ITEMS')}
			</h6>
			<Row gutter={[16, 16]}>
				<Form.List name="decisions">
					{(fields, { add, remove }) => (
						<>
							{fields.map(({ key, name, ...restField }, index) => (
								<Fragment key={key}>
									<Col span={24} md={11}>
										<Typography style={{ fontSize: 15, marginBottom: 5 }}>{t('ACTIONS.POINT_IN_AR')}</Typography>
										<Form.Item
											{...restField}
											name={[name, 'ar']}
											style={{ marginBottom: 0, maxWidth: 'unset' }}
											rules={[{ required: type === 'Voting', message: t('COMMON.FIELD_REQUIRED') }, { validator: validateArabic }]}>
											<TextArea autoSize={{ minRows: 1, maxRows: 4 }} />
										</Form.Item>
									</Col>

									<Col span={24} md={11}>
										<Typography style={{ fontSize: 15, marginBottom: 5 }}>{t('ACTIONS.POINT_IN_EN')}</Typography>
										<Form.Item
											{...restField}
											name={[name, 'en']}
											style={{ marginBottom: 0, maxWidth: 'unset' }}
											rules={[{ required: type === 'Voting', message: t('COMMON.FIELD_REQUIRED') }, { validator: validateEnglish }]}>
											<TextArea autoSize={{ minRows: 1, maxRows: 4 }} />
										</Form.Item>
									</Col>

									{index > 0 && (
										<Col span={24} md={2}>
											<div style={{ marginTop: 30 }}>
												<ListButton type="delete" onClick={() => handleDeleteDecision(name, remove)} />
											</div>
										</Col>
									)}

									<Col span={24}>
										<Row gutter={[16, 16]}>
											<Col span={24} md={8}>
												<SelectElement
													{...restField}
													name={[name, 'abstention_type']}
													label={t('ACTIONS.ABSTENTION_TYPE')}
													fullWidth
													rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
													options={[
														{ label: t('ACTIONS.NONE'), value: 'none' },
														// { label: t('ACTIONS.ALL_BOARD_MEMBERS'), value: 'all_board_members' },
														{ label: t('ACTIONS.SPECIFIC_SHAREHOLDERS'), value: 'specific_board_members' },
													]}
												/>
											</Col>

											{watchedDecisions?.[name]?.abstention_type === 'specific_board_members' && (
												<Col span={24} md={15}>
													<Form.Item
														{...restField}
														name={[name, 'abstaining_board_member_ids']}
														style={{ maxWidth: 'unset' }}
														rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}>
														<Select
															mode="multiple"
															style={{ width: '100%', marginTop: 28 }}
															placeholder={t('ACTIONS.SEARCH_BY_NATIONAL_ID_OR_NAME')}
															suffixIcon={<Icon name="search" />}
															options={getOptionsForDecision(name)}
															onChange={(values) => handleSelectChange(values, name)}
															onSearch={setSearchedQuery}
															searchValue={searchedQuery}
															loading={isSearching}
															showSearch
															filterOption={false}
															notFoundContent={
																isSearching ? (
																	<span>
																		<LoadingOutlined style={{ marginInlineEnd: 10, color: 'var(--primary)' }} /> {t('COMMON.SEARCHING')}
																	</span>
																) : (
																	t('COMMON.NO_DATA_FOUND')
																)
															}
														/>
													</Form.Item>
												</Col>
											)}
										</Row>
									</Col>

									{fields.length !== index + 1 && <Divider className="my-1" />}
								</Fragment>
							))}

							<Button type="link" className="p-0" onClick={() => add({
								abstention_type: 'none',
							})}>
								<Icon name="plus" width={13} height={13} color="var(--primary)" />
								{t('ACTIONS.ADD_ANOTHER_POINT')}
							</Button>
						</>
					)}
				</Form.List>
			</Row>
		</>
	);
}

export default CommitteeDecisionElement;
